import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HttpError, type CardId, type Priority } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { boardRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useOrgContext, useSnackbar } from '@web/ui/core';
import { fireAndForget } from '@web/ui/core/utils';

export interface UseCreateTask {
  isOpen: boolean;
  open: () => void;
  cancel: () => void;
  pending: boolean;
  create: (values: { description: string; priority: Priority }) => void;
}

/**
 * "Nova tarefa" flow for a card. Owns the form-open state + the `task.create`
 * mutation (assigneeAgent omitted → backend assigns to the caller). Mirrors
 * useCloseCard; failures surface via snackbar.
 */
export function useCreateTask(cardId: CardId): UseCreateTask {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [isOpen, setIsOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: (values: { description: string; priority: Priority }) =>
      boardRepository.dispatchCardEvent(orgId, cardId, {
        event: {
          type: 'task.create',
          description: values.description,
          priority: values.priority,
          start: true,
        },
      }),
    onSuccess: () => {
      setIsOpen(false);
      fireAndForget(
        queryClient.invalidateQueries({ queryKey: queryKeys.cards.tasks(orgId, cardId) }),
      );
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.boards.all }));
    },
    onError: (err: unknown) => {
      showSnackbar({
        severity: 'error',
        message:
          err instanceof HttpError || err instanceof Error
            ? err.message
            : t('board.drawer.tasks.createForm.error'),
      });
    },
  });

  return {
    isOpen,
    open: () => {
      setIsOpen(true);
    },
    cancel: () => {
      setIsOpen(false);
    },
    pending: mutation.isPending,
    create: (values) => {
      mutation.mutate(values);
    },
  };
}
